import { createRouter, createWebHistory } from 'vue-router';
import { watch } from 'vue';  // Add this import
import HomeView from '../views/HomeView.vue';
import EditorView from '../views/EditorView.vue';
import ViewerView from '../views/ViewerView.vue';
import { useAuth } from '../firebase/auth';
import LoginView from '../views/LoginView.vue';

const BASE_URL = process.env.NODE_ENV === 'production' ? '/' : '/';

const timeout = new Promise(resolve => setTimeout(resolve, 2000));

const requireAuth = async (to, from, next) => {
  const { user, loading } = useAuth();
  
  if (loading.value) {
    await Promise.race([
      new Promise(resolve => {
        const unsubscribe = watch(loading, (newValue) => {
          if (!newValue) {
            unsubscribe();
            resolve();
          }
        });
      }),
      timeout
    ]);
  }
  
  if (user.value) {
    next();
  } else {
    next('/login');
  }
};

const requireEditor = async (to, from, next) => {
  const { user, loading, canEdit, userRole } = useAuth();
  
  if (loading.value) {
    await Promise.race([
      new Promise(resolve => {
        const unsubscribe = watch([loading, userRole], ([newLoading, newRole]) => {
          if (!newLoading && newRole) {
            unsubscribe();
            resolve();
          }
        });
      }),
      timeout
    ]);
  }

  if (user.value && canEdit.value) {
    next();
  } else if (user.value) {
    next('/'); // Redirect to home if user doesn't have permission
  } else {
    next('/login');
  }
};

// Remove the unused requireAdmin function

const router = createRouter({
  history: createWebHistory(BASE_URL),
  routes: [
    {
      path: '/',
      name: 'Home',
      component: HomeView,
      beforeEnter: requireAuth
    },
    {
      path: '/editor/:id',
      name: 'Editor',
      component: EditorView,
      beforeEnter: requireEditor // Only editors can access this route
    },
    {
      path: '/viewer/:id/:type?',
      name: 'Viewer',
      component: ViewerView,
      beforeEnter: requireAuth
    },
    {
      path: '/in-progress',
      name: 'CompletedChecklists',
      component: () => import('../views/CompletedChecklistsView.vue'),
      beforeEnter: requireAuth
    },
    {
      path: '/pdfs',
      name: 'PDFs',
      component: () => import('../views/PDFListView.vue'),
      beforeEnter: requireAuth
    },
    {
      path: '/login',
      name: 'Login',
      component: LoginView
    },
    {
      path: '/settings',
      name: 'Settings',
      component: () => import('../views/SettingsView.vue'),
      beforeEnter: requireAuth // Just use requireAuth since we handle admin permissions in the component
    },
    {
      path: '/setup/:id',
      name: 'ChecklistSetup',
      component: () => import('../components/ChecklistSetup.vue'),
      meta: { requiresAuth: true },
      beforeEnter: requireAuth  // Add this line to ensure authentication
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: () => import('../views/ResetPasswordView.vue')
    }
  ]
});

export default router;